import { formatDate } from '@angular/common';
import {
  AtividadeToiPorAtividadeOutput,
  MxlAtividadeToiOutput,
  ToiJsonDados,
} from '../models/toi-api.model';
import { PdfToiChecklistItem, PdfToiIrregularidadeMarcarItem } from '../models/pdf-toi.model';
import { ToiEconomiaCategoriaRow, ToiIrregularidadeItem } from '../models/toi-detalhe.model';

/** Formata data para os query params da API (yyyy-MM-dd). */
export function formatarDataParamToi(date: Date): string {
  return formatDate(date, 'yyyy-MM-dd', 'en-US');
}

/** Mesma prioridade de fonte usada no detalhe e no PDF — evita dados vazios quando `dadosHst` vem incompleto. */
export function resolverDadosPorAtividade(
  response: AtividadeToiPorAtividadeOutput
): Record<string, unknown> | MxlAtividadeToiOutput | undefined {
  return (response.dadosMxl ?? response.dadosHst) as
    | Record<string, unknown>
    | MxlAtividadeToiOutput
    | undefined;
}

export function parseJsonToi(json: string | null | undefined): Record<string, unknown> | null {
  if (!json) {
    return null;
  }

  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Parse de `jsonDadosToi` normalizando as chaves em caixa alta gravadas pelo app mobile. */
export function parseJsonDadosToi(json: string | null | undefined): ToiJsonDados | null {
  const parsed = parseJsonToi(json);
  if (!parsed) {
    return null;
  }

  return {
    ...parsed,
    nrHidrometro: parsed['nrHidrometro'] ?? parsed['NR_HIDROMETRO'],
    nrLacre: parsed['nrLacre'] ?? parsed['NR_LACRE'],
    leituraRetirada: parsed['leituraRetirada'] ?? parsed['LEITURA_RETIRADA'],
    leituraInstalacao: parsed['leituraInstalacao'] ?? parsed['LEITURA_INSTALACAO'],
    localizacao: parsed['localizacao'] ?? parsed['LOCALIZACAO'],
    nomeUsuario: parsed['nomeUsuario'] ?? parsed['NOME_USUARIO'],
    economiaPorCategorias: (parsed['economiaPorCategorias'] ??
      parsed['ECONOMIA_POR_CATEGORIAS']) as ToiEconomiaCategoriaRow[] | undefined,
    irregularidades: (parsed['irregularidades'] ??
      parsed['IRREGULARIDADES'] ??
      []) as ToiIrregularidadeItem[],
    irregularidadeMarcar: (parsed['irregularidadeMarcar'] ??
      parsed['IRREGULARIDADE_MARCAR'] ??
      []) as PdfToiIrregularidadeMarcarItem[],
  };
}

/** Resolve a data de conclusão da OS a partir dos dados TOI (HST/MXL). */
export function resolveDataConclusao(
  dados?: Record<string, unknown> | null,
  dadosHst?: Record<string, unknown> | null
): string {
  const fromHst = String(dadosHst?.['dataConclusao'] ?? '').trim();
  const fromDados = String(dados?.['dataConclusao'] ?? '').trim();
  const fallback = String(dados?.['dataVistoria'] ?? dadosHst?.['dataVistoria'] ?? '').trim();
  return fromHst || fromDados || fallback;
}

export function mapChecklistToi(items: unknown[] | undefined): PdfToiChecklistItem[] {
  if (!items?.length) {
    return [];
  }

  return items.map((item) => {
    const row = item as Record<string, unknown>;
    return {
      idEntidade: Number(row['idEntidade'] ?? 0),
      guid: String(row['guid'] ?? ''),
      label: String(row['label'] ?? ''),
      valor: String(row['valor'] ?? ''),
      ordem: Number(row['ordem'] ?? 0),
      dataAtualizacao: String(row['dataAtualizacao'] ?? ''),
    };
  });
}

/** Converte célula numérica que a API pode devolver como texto. */
export function parseNumeroToi(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const numero = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(numero) ? numero : 0;
}

export function respostaToiPositiva(resposta: string | undefined | null): boolean {
  const valor = String(resposta ?? '').trim().toLowerCase();
  return valor === 'sim' || valor === 'x' || valor === 'true' || valor === '1';
}

/** Converte o envelope `statusRetorno: 'NOK'` da API em erro para o fluxo do RxJS. */
export function garantirRetornoToiOk<
  T extends { statusRetorno?: string | null; mensagemRetorno?: string | null },
>(resposta: T, mensagemPadrao = 'Não foi possível concluir a operação do TOI.'): T {
  if (resposta.statusRetorno === 'NOK') {
    throw new Error(resposta.mensagemRetorno || mensagemPadrao);
  }
  return resposta;
}
